import React from 'react';
import { useHistory, useLocation, useParams, Link } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { PageLayout } from '@lifeomic/chroma-react/components/PageLayout';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { Menu, MenuItem } from '@lifeomic/chroma-react/components/Menu';
import { IconButton } from '@lifeomic/chroma-react/components/IconButton';
import moment from 'moment';
import {
  Menu as MenuIcon,
  Maximize,
  Minimize,
  RefreshCw,
  SparkleCircle,
  X,
  ExternalLink,
  FileText,
  CheckList,
  Beaker,
  File,
  Clipboard,
  Helix,
  PlayCircle,
  Tablet,
  Procedure,
} from '@lifeomic/chromicons';
import { Button } from '@lifeomic/chroma-react/components/Button';
import { ButtonUnstyled } from '@lifeomic/chroma-react/components/ButtonUnstyled';
import { DotLoader } from '@lifeomic/chroma-react/components/DotLoader';
import {
  Radio,
  RadioGroupMinimal,
} from '@lifeomic/chroma-react/components/Radio';
import { Tabs, TabList, Tab } from '@lifeomic/chroma-react/components/Tabs';
import { Tooltip } from '@lifeomic/chroma-react/components/Tooltip';
import { useSelector } from 'react-redux';
import { useMaskedMode } from '@lifeomic/phc-web-toolkit/src/hooks/useMaskedMode';
import EnforceAccountFeatureToggle from '@lifeomic/phc-web-toolkit/src/components/EnforceAccountFeatureToggle';
import {
  useActiveAccountId,
  useActiveUserId,
  useActiveProjectCohortId,
  useActiveProjectId,
  useFeatures,
} from '@lifeomic/phc-web-toolkit/src/hooks';
import {
  persistRecentConversation,
  RecentConversationStorageEntry,
} from '@lifeomic/phc-ui-qa-agent/src/hooks/useRecentConversations';
import { useOmnibarContext } from '@lifeomic/phc-web-toolkit/src/components/Omnibar/OmnibarProvider';
import { selectActiveProject } from '@lifeomic/phc-web-toolkit/src/redux/modules/projectPicker/selectors';
import urls from '../../../../util/urls';
import { isFountainLifeAccountId } from '../../../../util/fountainLifeAccount';
import { Resizable } from 'react-resizable';
import 'react-resizable/css/styles.css';
import { Chat } from '@lifeomic/phc-ui-qa-agent/src/components/Chat';
import { CLINICIANS_AGENT_USERNAME } from '@lifeomic/phc-ui-qa-agent/src/constants';
import { MemberChatHistory } from '@lifeomic/phc-ui-qa-agent/src/components/MemberChatHistory';
import { AttachmentViewerProvider } from '@lifeomic/phc-web-toolkit/src/components/Messaging/AttachmentViewer';
import { AnnualReportContent } from './AnnualReport';
import { ActionPlanContent } from './ActionPlan';
import { ZoriReportsContent } from './AiTools';
import { DocumentsContent } from './Documents';
import { LabsContent } from './Labs';
import { OmicsExplorerContent } from './OmicsExplorer';
import { OnboardingSnapshotContent } from './OnboardingSnapshotContent';
import { SurveysContent } from './Surveys';
import { memberDetailMessages } from './messages';
import { MemberSection, MEMBER_SECTION_STORAGE_KEY } from './types';
import { Link as ChromaLink } from '@lifeomic/chroma-react/components/Link';
import { useMemberDetailData } from './useMemberDetailData';
import { useMemberOutsideActiveCohort } from './useMemberOutsideActiveCohort';
import { useMemberDetailOmnibarSuggestions } from './useMemberDetailOmnibarSuggestions';
import { useMemberDetailStyles } from './styles';
import { useCurrentMemberNav } from './CurrentMemberNavContext';
import clsx from 'clsx';
import { WithPlatformDefaults } from '@lifeomic/react-client';
import useService from '../../../../services/util/useService';
import SubjectsSearchService from '../../../../services/SubjectsSearchService';
import { getElationPatientId } from '../../../../util/elation';
import { MemberAppContent } from './MemberApp';
import MemberCohortMismatchBanner from './MemberCohortMismatchBanner';
import { RuleActionAuditContent } from './RuleActionAudit';
import {
  areAppBarsHidden,
  toggleAppBarsHidden,
} from '@lifeomic/phc-web-toolkit/src/util/appBarsVisibility';

const useStyles = useMemberDetailStyles;
const subjectsSearchService = new SubjectsSearchService();

const ZORI_CHAT_OPEN_STORAGE_KEY_PREFIX = 'member-detail.zori-chat-open';

const calculateAge = (member: fhir.Patient | null): number | undefined => {
  return member?.birthDate
    ? moment().diff(member.birthDate, 'years')
    : undefined;
};

const getMembershipTier = (member: fhir.Patient | null): string | undefined => {
  return member?.meta?.tag
    ?.find(
      tag =>
        tag.system === 'http://lifeomic.com/fhir/fountain-life-membership-tier',
    )
    ?.code?.toUpperCase();
};

const getZoriChatStorageKey = (memberId: string) =>
  `${ZORI_CHAT_OPEN_STORAGE_KEY_PREFIX}:${memberId}`;

const getStoredZoriChatOpenState = (memberId: string): boolean => {
  try {
    return (
      window.localStorage.getItem(getZoriChatStorageKey(memberId)) === 'true'
    );
  } catch (error) {
    return false;
  }
};

const POWER_USER_SECTION_ICONS: Record<
  MemberSection,
  React.ComponentType<{ className?: string }>
> = {
  'onboarding-snapshot': PlayCircle,
  'annual-report': FileText,
  'action-plan': CheckList,
  'ai-tools': SparkleCircle,
  'rule-action-audit': Procedure,
  labs: Beaker,
  documents: File,
  surveys: Clipboard,
  'omics-explorer': Helix,
  'member-app': Tablet,
};

export interface MemberDetailProps {
  section?: MemberSection;
}

const MemberDetail: React.FC<MemberDetailProps> = ({ section }) => {
  const { formatMessage } = useIntl();
  const classes = useStyles({});
  const { memberId } = useParams<{ memberId: string }>();
  const history = useHistory();
  const location = useLocation();
  const accountId = useActiveAccountId();
  const activeUserId = useActiveUserId();
  const activeProjectCohortId = useActiveProjectCohortId();
  const activeProjectId = useActiveProjectId();
  const hasFeature = useFeatures();
  const fountainLifeUXEnabled =
    hasFeature('fountainLifeUX') || isFountainLifeAccountId(accountId);
  const fountainLifePowerUserEnabled = hasFeature('fountainLifePowerUser');
  const memberAppViewingEnabled = hasFeature('memberAppViewing');
  const isMasked = useMaskedMode();
  const activeProject = useSelector(selectActiveProject);
  const mrnConfig = activeProject?.mrnConfig;
  const [resolveMemberByIdentifier] = useService(
    subjectsSearchService.getSubjects,
  );
  const [isResolvingMemberId, setIsResolvingMemberId] = React.useState(false);
  const isElationMemberId = /^\d+$/.test(memberId);
  const { searchValue, registerSuggestions, unregisterSuggestions } =
    useOmnibarContext();
  const [showZoriChat, setShowZoriChat] = React.useState(() =>
    getStoredZoriChatOpenState(memberId),
  );
  const [chatWidth, setChatWidth] = React.useState(420);
  const [isChatFullscreen, setIsChatFullscreen] = React.useState(false);
  const [isAppChromeHidden, setIsAppChromeHidden] = React.useState(() =>
    areAppBarsHidden(),
  );
  const prevShowZoriChat = React.useRef(false);
  const [chatMode, setChatMode] = React.useState<'clinician' | 'member'>(
    'clinician',
  );

  React.useEffect(() => {
    let isCancelled = false;

    const maybeRedirectElationMemberId = async () => {
      if (!isElationMemberId || !activeProject?.id) {
        return;
      }

      setIsResolvingMemberId(true);
      try {
        const response = await resolveMemberByIdentifier({
          projectId: activeProject.id,
          identifier: [memberId],
        });
        const entries = response?.data?.patients?.entry || [];
        const matchingEntry = entries.find(
          (entry: any) => getElationPatientId(entry?.resource) === memberId,
        );
        const resolvedMemberId =
          matchingEntry?.resource?.id || entries?.[0]?.resource?.id;

        if (!resolvedMemberId || resolvedMemberId === memberId) {
          return;
        }

        const memberBasePath = urls.app.members(memberId);
        if (!location.pathname.startsWith(memberBasePath)) {
          return;
        }

        const pathSuffix = location.pathname.slice(memberBasePath.length);
        history.replace(
          `${urls.app.members(resolvedMemberId)}${pathSuffix}${
            location.search
          }${location.hash}`,
        );
      } finally {
        if (!isCancelled) {
          setIsResolvingMemberId(false);
        }
      }
    };

    maybeRedirectElationMemberId();

    return () => {
      isCancelled = true;
    };
  }, [
    activeProject?.id,
    history,
    isElationMemberId,
    location.hash,
    location.pathname,
    location.search,
    memberId,
    resolveMemberByIdentifier,
  ]);

  React.useEffect(() => {
    if (!section && memberId) {
      const storedSection =
        (window.localStorage.getItem(
          MEMBER_SECTION_STORAGE_KEY,
        ) as MemberSection) || 'annual-report';
      history.replace(`${urls.app.members(memberId)}/${storedSection}`);
    }
  }, [history, memberId, section]);

  React.useEffect(() => {
    if (section) {
      window.localStorage.setItem(MEMBER_SECTION_STORAGE_KEY, section);
    }
  }, [section]);

  React.useEffect(() => {
    setShowZoriChat(getStoredZoriChatOpenState(memberId));
    setIsChatFullscreen(false);
  }, [memberId]);

  React.useEffect(() => {
    try {
      window.localStorage.setItem(
        getZoriChatStorageKey(memberId),
        showZoriChat ? 'true' : 'false',
      );
    } catch (error) {
      // Ignore storage failures; preserving chat state is best-effort.
    }
  }, [memberId, showZoriChat]);

  React.useEffect(() => {
    if (!isChatFullscreen) {
      return;
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsChatFullscreen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isChatFullscreen]);

  React.useEffect(() => {
    const root = document.documentElement;
    const observer = new MutationObserver(() => {
      setIsAppChromeHidden(areAppBarsHidden());
    });

    observer.observe(root, {
      attributes: true,
      attributeFilter: ['data-app-bars-hidden'],
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  const {
    member,
    memberDisplayName,
    formattedBirthDate,
    elationChartUrl,
    memberLoadError,
    memberNotFound,
    hasLoadedMember,
    isLoading,
    canSync,
    refreshEhrData,
    refreshMember,
  } = useMemberDetailData({
    accountId,
    memberId,
    isMasked,
    mrnConfig,
  });

  const cohortMismatch = useMemberOutsideActiveCohort({
    projectId: activeProjectId,
    patientId: member?.id,
    activeCohortId: activeProjectCohortId || null,
  });

  const age = calculateAge(member);
  const membershipTier = getMembershipTier(member);
  const { setMemberDisplayName } = useCurrentMemberNav();

  React.useEffect(() => {
    setMemberDisplayName(memberDisplayName ?? null);
    return () => setMemberDisplayName(null);
  }, [memberDisplayName, setMemberDisplayName]);

  useMemberDetailOmnibarSuggestions({
    fountainLifeUXEnabled,
    fountainLifePowerUserEnabled,
    memberId,
    memberDisplayName,
    section,
    formatMessage,
    searchValue,
    registerSuggestions,
    unregisterSuggestions,
    setShowZoriChat,
  });

  React.useEffect(() => {
    const shouldAddToRecent =
      showZoriChat && !prevShowZoriChat.current && member && activeUserId;
    prevShowZoriChat.current = showZoriChat;

    if (!shouldAddToRecent) return;

    const userIds = [
      activeUserId,
      CLINICIANS_AGENT_USERNAME,
      `scope?subject=${member.id}`,
    ];
    const entry: RecentConversationStorageEntry = {
      conversationId: '',
      patientId: member.id,
      title: memberDisplayName || undefined,
      lastOpenedAt: new Date().toISOString(),
      userIds,
    };

    persistRecentConversation(entry);
  }, [showZoriChat, member, activeUserId, memberDisplayName]);

  const openMemberSectionInNewTab = React.useCallback(
    (path: string) => {
      const fullUrl = `${window.location.origin}${history.createHref({
        pathname: path,
      })}`;
      window.open(fullUrl, '_blank');
    },
    [history],
  );

  if (!section) {
    return null;
  }

  if (isElationMemberId && isResolvingMemberId) {
    return (
      <Box fullWidth fullHeight align="center" justify="center">
        <DotLoader />
      </Box>
    );
  }

  const contentMessage = (() => {
    switch (section) {
      default:
        return memberDetailMessages.contentPlaceholder;
    }
  })();

  const getMemberSectionUrl = (targetSection: MemberSection) =>
    `${urls.app.members(memberId)}/${targetSection}`;

  const getSectionLabel = (targetSection: MemberSection): string => {
    switch (targetSection) {
      case 'onboarding-snapshot':
        return formatMessage(memberDetailMessages.onboardingSnapshotLabel);
      case 'annual-report':
        return formatMessage(memberDetailMessages.annualReportLabel);
      case 'action-plan':
        return formatMessage(memberDetailMessages.actionPlanLabel);
      case 'ai-tools':
        return formatMessage(memberDetailMessages.aiToolsLabel);
      case 'rule-action-audit':
        return formatMessage(memberDetailMessages.ruleActionAuditLabel);
      case 'labs':
        return formatMessage(memberDetailMessages.labsLabel);
      case 'surveys':
        return formatMessage(memberDetailMessages.surveysLabel);
      case 'documents':
        return formatMessage(memberDetailMessages.documentsLabel);
      case 'omics-explorer':
        return formatMessage(memberDetailMessages.omicsExplorerLabel);
      case 'member-app':
        return formatMessage(memberDetailMessages.memberAppLabel);
      default:
        return '';
    }
  };

  const sectionLabel = getSectionLabel(section);

  const memberLoadReasonMessage = memberNotFound
    ? memberDetailMessages.memberNotFound
    : memberDetailMessages.memberUnavailable;

  const loadingFallback =
    isLoading || (!member && !hasLoadedMember && !memberLoadError) ? (
      <Box fullWidth fullHeight align="center" justify="center" gap={1}>
        <DotLoader />
      </Box>
    ) : (
      <Text size="subbody">{formatMessage(memberLoadReasonMessage)}</Text>
    );

  const menuItems = [
    <Link
      key="onboarding-snapshot"
      to={getMemberSectionUrl('onboarding-snapshot')}
      className={clsx(
        classes.menuItemLink,
        section === 'onboarding-snapshot' && classes.menuItemSelected,
      )}
    >
      <MenuItem className={classes.menuItemWrapper}>
        {formatMessage(memberDetailMessages.onboardingSnapshot)}
      </MenuItem>
    </Link>,
    <Link
      key="annual-report"
      to={getMemberSectionUrl('annual-report')}
      className={clsx(
        classes.menuItemLink,
        section === 'annual-report' && classes.menuItemSelected,
      )}
    >
      <MenuItem className={classes.menuItemWrapper}>
        {formatMessage(memberDetailMessages.annualReport)}
      </MenuItem>
    </Link>,
    <Link
      key="action-plan"
      to={getMemberSectionUrl('action-plan')}
      className={`${classes.menuItemLink} ${
        section === 'action-plan' ? classes.menuItemSelected : ''
      }`}
    >
      <MenuItem className={classes.menuItemWrapper}>
        {formatMessage(memberDetailMessages.actionPlan)}
      </MenuItem>
    </Link>,
    <Link
      key="ai-tools"
      to={getMemberSectionUrl('ai-tools')}
      className={`${classes.menuItemLink} ${
        section === 'ai-tools' ? classes.menuItemSelected : ''
      }`}
    >
      <MenuItem className={classes.menuItemWrapper}>
        {formatMessage(memberDetailMessages.aiTools)}
      </MenuItem>
    </Link>,
    <Link
      key="labs"
      to={getMemberSectionUrl('labs')}
      className={`${classes.menuItemLink} ${
        section === 'labs' ? classes.menuItemSelected : ''
      }`}
    >
      <MenuItem className={classes.menuItemWrapper}>
        {formatMessage(memberDetailMessages.labsAndData)}
      </MenuItem>
    </Link>,
    <Link
      key="documents"
      to={getMemberSectionUrl('documents')}
      className={`${classes.menuItemLink} ${
        section === 'documents' ? classes.menuItemSelected : ''
      }`}
    >
      <MenuItem className={classes.menuItemWrapper}>
        {formatMessage(memberDetailMessages.documents)}
      </MenuItem>
    </Link>,
    <Link
      key="surveys"
      to={getMemberSectionUrl('surveys')}
      className={`${classes.menuItemLink} ${
        section === 'surveys' ? classes.menuItemSelected : ''
      }`}
    >
      <MenuItem className={classes.menuItemWrapper}>
        {formatMessage(memberDetailMessages.surveys)}
      </MenuItem>
    </Link>,
  ];
  if (memberAppViewingEnabled) {
    menuItems.push(
      <Link
        key="member-app"
        to={getMemberSectionUrl('member-app')}
        className={`${classes.menuItemLink} ${
          section === 'member-app' ? classes.menuItemSelected : ''
        }`}
      >
        <MenuItem className={classes.menuItemWrapper}>
          {formatMessage(memberDetailMessages.memberAppLabel)}
        </MenuItem>
      </Link>,
    );
  }
  if (fountainLifePowerUserEnabled) {
    menuItems.push(
      <Link
        key="rule-action-audit"
        to={getMemberSectionUrl('rule-action-audit')}
        className={`${classes.menuItemLink} ${
          section === 'rule-action-audit' ? classes.menuItemSelected : ''
        }`}
      >
        <MenuItem className={classes.menuItemWrapper}>
          {formatMessage(memberDetailMessages.ruleActionAudit)}
        </MenuItem>
      </Link>,
    );
  }
  menuItems.push(
    <EnforceAccountFeatureToggle accountFeature="genomics" key="omics-explorer">
      <Link
        to={getMemberSectionUrl('omics-explorer')}
        className={`${classes.menuItemLink} ${
          section === 'omics-explorer' ? classes.menuItemSelected : ''
        }`}
      >
        <MenuItem className={classes.menuItemWrapper}>
          {formatMessage(memberDetailMessages.omicsExplorer)}
        </MenuItem>
      </Link>
    </EnforceAccountFeatureToggle>,
  );

  let chatWrapperClassName = classes.chatWrapper;
  if (isChatFullscreen) {
    chatWrapperClassName = `${classes.chatWrapper} ${classes.chatWrapperFullscreen}`;
  } else {
    chatWrapperClassName = `${classes.chatWrapper} ${classes.chatWrapperOverlay}`;
  }

  const chatModeSelector = (
    <RadioGroupMinimal
      aria-label="Chat Selector"
      name="memberDetailChatMode"
      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
        setChatMode(event.target.value as 'clinician' | 'member');
      }}
      value={chatMode}
    >
      <Radio
        value="clinician"
        label={formatMessage(memberDetailMessages.chatClinicianLabel)}
      />
      <Radio
        value="member"
        label={formatMessage(memberDetailMessages.chatMemberHistoryLabel)}
      />
    </RadioGroupMinimal>
  );

  let chatContent = null;
  if (member) {
    chatContent =
      chatMode === 'clinician' ? (
        <Chat patient={member} />
      ) : (
        <MemberChatHistory patient={member} />
      );
  }

  return (
    <PageLayout
      maxWidth
      applyBackgroundCover
      classes={{
        root: classes.pageRoot,
        layoutContainer: classes.layoutContainer,
        headerBackgroundPadding: classes.headerBackgroundPadding,
        backgroundCover: classes.backgroundCover,
        content: classes.contentArea,
      }}
      left={
        <Box className={classes.header}>
          {isLoading || (!member && !hasLoadedMember && !memberLoadError) ? (
            <Text size="subbody" weight="bold" className={classes.headerName}>
              {formatMessage(memberDetailMessages.loadingMember)}
            </Text>
          ) : !member ? (
            <Text size="subbody" weight="bold" className={classes.headerName}>
              {formatMessage(memberLoadReasonMessage)}
            </Text>
          ) : (
            <>
              <Box className={classes.headerName} gap={1} direction="column">
                <Text size="subbody" weight="bold">
                  {memberDisplayName ||
                    formatMessage(memberDetailMessages.memberNameUnavailable)}
                </Text>
                <Box className={classes.headerMeta}>
                  {formattedBirthDate && (
                    <Text size="subbody" className={classes.headerMetaItem}>
                      DOB: {formattedBirthDate}
                    </Text>
                  )}
                  {age !== undefined && (
                    <Text size="subbody" className={classes.headerMetaItem}>
                      Age: {age}
                    </Text>
                  )}
                  {membershipTier && (
                    <Text size="subbody" className={classes.headerMetaItem}>
                      Tier: {membershipTier}
                    </Text>
                  )}
                  {elationChartUrl && (
                    <ChromaLink
                      className={classes.headerMetaItem}
                      to={elationChartUrl}
                      newTab
                    >
                      <span className={classes.elationLinkContent}>
                        {formatMessage(memberDetailMessages.elationLink)}
                        <ExternalLink style={{ width: 14, height: 14 }} />
                      </span>
                    </ChromaLink>
                  )}
                </Box>
              </Box>
            </>
          )}
        </Box>
      }
      headerCenter={
        isAppChromeHidden ? null : (
          <Box className={classes.menuActions}>
            {fountainLifePowerUserEnabled ? (
              <Tabs selectedTabId={section} manualControl>
                <TabList
                  aria-label={formatMessage(memberDetailMessages.menuLabel)}
                  className={classes.sectionTabList}
                >
                  {(
                    [
                      'onboarding-snapshot',
                      'annual-report',
                      'action-plan',
                      'ai-tools',
                      'rule-action-audit',
                      'labs',
                      'documents',
                      'surveys',
                      ...(memberAppViewingEnabled
                        ? (['member-app'] as const)
                        : []),
                    ] as const
                  ).map(tabSection => {
                    const IconComponent = POWER_USER_SECTION_ICONS[tabSection];
                    const url = getMemberSectionUrl(tabSection);
                    const label = getSectionLabel(tabSection);
                    return (
                      <Tooltip key={tabSection} title={label}>
                        <span className={classes.sectionTabTooltipWrapper}>
                          <Tab
                            stopId={tabSection}
                            onClick={(e: React.MouseEvent) => {
                              if (e.metaKey || e.ctrlKey) {
                                openMemberSectionInNewTab(url);
                              } else {
                                history.push(url);
                              }
                            }}
                            className={classes.sectionTab}
                            aria-label={label}
                          >
                            <IconComponent className={classes.sectionTabIcon} />
                          </Tab>
                        </span>
                      </Tooltip>
                    );
                  })}
                  <EnforceAccountFeatureToggle
                    accountFeature="genomics"
                    key="omics-explorer"
                  >
                    <Tooltip title={getSectionLabel('omics-explorer')}>
                      <span className={classes.sectionTabTooltipWrapper}>
                        <Tab
                          stopId="omics-explorer"
                          onClick={(e: React.MouseEvent) => {
                            const url = getMemberSectionUrl('omics-explorer');
                            if (e.metaKey || e.ctrlKey) {
                              openMemberSectionInNewTab(url);
                            } else {
                              history.push(url);
                            }
                          }}
                          className={classes.sectionTab}
                          aria-label={getSectionLabel('omics-explorer')}
                        >
                          <Helix className={classes.sectionTabIcon} />
                        </Tab>
                      </span>
                    </Tooltip>
                  </EnforceAccountFeatureToggle>
                </TabList>
              </Tabs>
            ) : (
              <Menu
                aria-label={formatMessage(memberDetailMessages.menuLabel)}
                anchorElement={
                  <ButtonUnstyled
                    aria-label={formatMessage(memberDetailMessages.menuLabel)}
                    className={classes.menuButton}
                  >
                    <MenuIcon />
                    <Text size="caption" className={classes.menuLabelText}>
                      {sectionLabel ||
                        formatMessage(memberDetailMessages.menuLabel)}
                    </Text>
                  </ButtonUnstyled>
                }
                placement="bottom"
                items={menuItems}
              />
            )}
          </Box>
        )
      }
      headerActions={
        isAppChromeHidden ? (
          <Box className={classes.headerActions}>
            <Button
              variant={isAppChromeHidden ? 'contained' : 'outlined'}
              icon={isAppChromeHidden ? X : Maximize}
              data-testid="member-detail-zori-chat-fullscreen-toggle"
              onClick={() => {
                const nextIsHidden = toggleAppBarsHidden();
                setIsAppChromeHidden(nextIsHidden);
              }}
            >
              Fullscreen
            </Button>
          </Box>
        ) : (
          <Box className={classes.headerActions}>
            <Button
              variant="outlined"
              icon={RefreshCw}
              onClick={canSync ? refreshEhrData : undefined}
              disabled={!canSync}
            >
              Sync
            </Button>
            <Button
              variant={showZoriChat ? 'contained' : 'outlined'}
              icon={SparkleCircle}
              data-testid="member-detail-zori-chat-toggle"
              onClick={() => {
                setShowZoriChat(prev => {
                  const next = !prev;
                  if (!next) {
                    setIsChatFullscreen(false);
                  }
                  return next;
                });
              }}
            >
              Zori
            </Button>
            <Button
              variant={isAppChromeHidden ? 'contained' : 'outlined'}
              icon={isAppChromeHidden ? X : Maximize}
              data-testid="member-detail-zori-chat-fullscreen-toggle"
              onClick={() => {
                const nextIsHidden = toggleAppBarsHidden();
                setIsAppChromeHidden(nextIsHidden);
              }}
            >
              Fullscreen
            </Button>
          </Box>
        )
      }
    >
      <Box className={classes.detailBody}>
        {member && activeProjectCohortId && cohortMismatch.showMismatch ? (
          <Box className={classes.cohortMismatchBanner}>
            <MemberCohortMismatchBanner
              show={cohortMismatch.showMismatch}
              homeCohort={cohortMismatch.homeCohort}
              isFindingHomeCohort={cohortMismatch.isFindingHomeCohort}
            />
          </Box>
        ) : null}
        <Box className={classes.contentLayout}>
          <Box
            className={classes.mainContent}
            data-testid={`member-section-${section}`}
          >
            {section === 'onboarding-snapshot' ? (
              <OnboardingSnapshotContent
                member={member as WithPlatformDefaults<fhir3.Patient>}
                memberId={memberId}
                fallback={loadingFallback}
                onMemberPatientRefetched={refreshMember}
              />
            ) : section === 'annual-report' ? (
              <AnnualReportContent
                member={member}
                memberId={memberId}
                fallback={loadingFallback}
              />
            ) : section === 'action-plan' ? (
              <ActionPlanContent
                member={member}
                memberId={memberId}
                fallback={loadingFallback}
              />
            ) : section === 'labs' ? (
              <LabsContent member={member} fallback={loadingFallback} />
            ) : section === 'ai-tools' ? (
              <ZoriReportsContent
                member={member}
                memberId={memberId}
                fallback={loadingFallback}
              />
            ) : section === 'rule-action-audit' ? (
              <RuleActionAuditContent memberId={memberId} />
            ) : section === 'documents' ? (
              <DocumentsContent member={member} fallback={loadingFallback} />
            ) : section === 'surveys' ? (
              <SurveysContent member={member} fallback={loadingFallback} />
            ) : section === 'omics-explorer' ? (
              <OmicsExplorerContent memberId={memberId} />
            ) : section === 'member-app' ? (
              <MemberAppContent member={member} fallback={loadingFallback} />
            ) : (
              <Text size="subbody">{formatMessage(contentMessage)}</Text>
            )}
          </Box>
          {showZoriChat && member && (
            <Box className={chatWrapperClassName}>
              {isChatFullscreen ? (
                <div className={classes.chatResizableFullscreen}>
                  <Box
                    className={`${classes.chatSidebar} ${classes.chatSidebarFullscreen}`}
                  >
                    <Box
                      className={classes.chatHeader}
                      align="center"
                      justify="space-between"
                      paddingX={2}
                      paddingY={1}
                    >
                      <Box align="center" gap={1}>
                        <Text size="subbody" weight="bold">
                          Zori
                        </Text>
                      </Box>
                      {chatModeSelector}
                      <Box align="center" gap={1}>
                        {fountainLifeUXEnabled && (
                          <IconButton
                            size={0}
                            aria-label="Collapse"
                            icon={Minimize}
                            onClick={() => setIsChatFullscreen(prev => !prev)}
                          />
                        )}
                        <IconButton
                          size={0}
                          aria-label="Close Zori"
                          icon={X}
                          onClick={() => setShowZoriChat(false)}
                        />
                      </Box>
                    </Box>
                    <Box className={classes.chatContent}>
                      <AttachmentViewerProvider>
                        {chatContent}
                      </AttachmentViewerProvider>
                    </Box>
                  </Box>
                </div>
              ) : (
                <Resizable
                  width={chatWidth}
                  height={0}
                  onResize={(_, { size }) => setChatWidth(size.width)}
                  resizeHandles={['w']}
                  minConstraints={[320, 0]}
                  maxConstraints={[640, 0]}
                  handle={<div className={classes.resizeHandle} />}
                >
                  <div
                    className={classes.chatResizable}
                    style={{ width: chatWidth }}
                  >
                    <Box className={classes.chatSidebar}>
                      <Box
                        className={classes.chatHeader}
                        align="center"
                        justify="space-between"
                        paddingX={2}
                        paddingY={1}
                      >
                        <Box align="center" gap={1}>
                          <Text size="subbody" weight="bold">
                            Zori
                          </Text>
                        </Box>
                        {chatModeSelector}
                        <Box align="center" gap={1}>
                          {fountainLifeUXEnabled && (
                            <IconButton
                              size={0}
                              aria-label="Expand"
                              icon={Maximize}
                              onClick={() => setIsChatFullscreen(true)}
                            />
                          )}
                          <IconButton
                            size={0}
                            aria-label="Close Zori"
                            icon={X}
                            onClick={() => setShowZoriChat(false)}
                          />
                        </Box>
                      </Box>
                      <Box className={classes.chatContent}>
                        <AttachmentViewerProvider>
                          {chatContent}
                        </AttachmentViewerProvider>
                      </Box>
                    </Box>
                  </div>
                </Resizable>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </PageLayout>
  );
};

MemberDetail.displayName = 'MemberDetail';
export default MemberDetail;
